import React, { useState } from 'react';
import { useForm, Controller, useFieldArray } from 'react-hook-form';
import { useNavigate } from 'react-router-dom';
import { useTranslation } from 'react-i18next';
import api from '../api';

// Shadcn UI Components
import { Button } from '../components/ui/button';
import { Card } from '../components/ui/card';
import { Input } from '../components/ui/input';
import { Tabs, TabsList, TabsTrigger, TabsContent } from '../components/ui/tabs';
import { Textarea } from '../components/ui/textarea';
import { Trash2 } from 'lucide-react';
import { Separator } from '../components/ui/separator';

export default function BuyerForm({ mode = 'create', defaults = {}, id }) {
  const navigate = useNavigate();
  const { t } = useTranslation();
  const [activeTab, setActiveTab] = useState("basic");
  const { control, handleSubmit, reset } = useForm({ defaultValues: defaults });
  const reps = useFieldArray({ control, name: 'representantes' });

  const onSubmit = data => {
    const req = mode === 'create'
      ? api.post('/compradores', data)
      : api.put(`/compradores/${id}`, data);
    req.then(() => { reset(); navigate('/admin/compradores'); })
      .catch(err => alert(err.response?.data?.error || err.message));
  };

  return (
     <div className="p-6 max-w-7xl mx-auto">
        <div className="flex justify-between items-center mb-8">
          <div>
            <h1 className="text-3xl font-bold tracking-tight">
              {mode === 'create' ? t('buyerForm.title') : t('buyerForm.editTitle')}
            </h1>
            <p className="text-muted-foreground mt-1">
              {mode === 'create' 
                ? 'Cadastre um novo comprador no sistema' 
                : 'Edite as informações do comprador'}
            </p>
          </div>
        </div>

        <form onSubmit={handleSubmit(onSubmit)}>
          <Tabs 
            value={activeTab} 
            onValueChange={setActiveTab} 
            className="w-full"
          >
            <TabsList className="mb-8 w-full justify-start">
              <TabsTrigger value="basic" className="px-6">{t('buyerForm.tabs.basic')}</TabsTrigger>
              <TabsTrigger value="representatives" className="px-6">{t('buyerForm.tabs.representatives')}</TabsTrigger>
              <TabsTrigger value="operational" className="px-6">{t('buyerForm.tabs.operational')}</TabsTrigger>
            </TabsList>

            {/* Tab: Informações Básicas */}
            <TabsContent value="basic" className="space-y-8">
              <Card className="p-6">
                <h3 className="text-lg font-semibold mb-6">{t('buyerForm.sections.companyInfo')}</h3>
                <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
                  {[
                    ['nomeFantasia', t('buyerForm.fields.companyName'), true],
                    ['razaoSocial', t('buyerForm.fields.legalName'), true],
                    ['cnpj', t('buyerForm.fields.cnpj'), true],
                    ['inscricaoEstadual', t('buyerForm.fields.stateRegistration'), false],
                    ['grupoEmpresarial', t('buyerForm.fields.businessGroup'), false, 2]
                  ].map(([name, label, required, colSpan = 1]) => (
                    <div key={name} className={colSpan === 2 ? 'md:col-span-2' : ''}>
                      <Controller
                        name={name}
                        control={control}
                        rules={required ? { required: true } : undefined}
                        render={({ field }) => (
                          <div className="space-y-2">
                            <label htmlFor={name} className="text-sm font-semibold text-foreground">
                              {label} {required && <span className="text-destructive">*</span>}
                            </label>
                            <Input 
                              {...field} 
                              id={name} 
                              className="h-11" 
                            />
                          </div>
                        )}
                      />
                    </div>
                  ))}
                </div>
              </Card>

              <Card className="p-6">
                <h3 className="text-lg font-semibold mb-6">{t('buyerForm.sections.address')}</h3>
                
                <div className="grid grid-cols-1 md:grid-cols-12 gap-6">
                  {[
                    ['cep', t('buyerForm.fields.zipCode'), 3], 
                    ['logradouro', t('buyerForm.fields.street'), 6], 
                    ['numero', t('buyerForm.fields.number'), 3],
                    ['complemento', t('buyerForm.fields.complement'), 4], 
                    ['bairro', t('buyerForm.fields.district'), 4], 
                    ['cidade', t('buyerForm.fields.city'), 3], 
                    ['estado', t('buyerForm.fields.state'), 1]
                  ].map(([key, label, cols]) => (
                    <div key={key} className={`md:col-span-${cols}`}>
                      <Controller
                        name={`endereco.${key}`}
                        control={control}
                        render={({ field }) => (
                          <div className="space-y-2">
                            <label htmlFor={`endereco-${key}`} className="text-sm font-semibold text-foreground">
                              {label}
                            </label>
                            <Input 
                              {...field} 
                              id={`endereco-${key}`}
                              className="h-11" 
                            />
                          </div>
                        )}
                      />
                    </div>
                  ))}
                </div>
              </Card>

              <Card className="p-6">
                <h3 className="text-lg font-semibold mb-6">{t('buyerForm.sections.mainContact')}</h3>
                
                <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
                  {[
                    {key: 'nome', label: t('buyerForm.fields.name')},
                    {key: 'telefone', label: t('buyerForm.fields.phone')},
                    {key: 'celular', label: t('buyerForm.fields.mobile')},
                    {key: 'email', label: t('buyerForm.fields.email')}
                  ].map(({key, label}) => (
                    <div key={key}>
                      <Controller
                        name={`contatoPrincipal.${key}`}
                        control={control}
                        render={({ field }) => (
                          <div className="space-y-2">
                            <label htmlFor={`contato-${key}`} className="text-sm font-semibold text-foreground">
                              {label}
                            </label>
                            <Input 
                              {...field} 
                              id={`contato-${key}`}
                              className="h-11" 
                            />
                          </div>
                        )}
                      />
                    </div>
                  ))}
                </div>
              </Card>
            </TabsContent>

            {/* Tab: Representantes */}
            <TabsContent value="representatives" className="space-y-6">
              <Card className="p-6">
                <div className="flex justify-between items-center mb-6">
                  <h3 className="text-lg font-semibold">{t('buyerForm.sections.representatives')}</h3>
                  <Button
                    type="button"
                    variant="outline" 
                    onClick={() => reps.append({ nome: '', departamento: '', celular: '', email: '' })}
                  >
                    {t('buyerForm.actions.addRepresentative')}
                  </Button>
                </div>
              
                {reps.fields.length === 0 ? (
                  <div className="text-center py-12 text-muted-foreground">
                    <p>Nenhum representante cadastrado</p>
                    <p className="text-sm mt-1">Clique no botão acima para adicionar</p>
                  </div>
                ) : (
                  <div className="space-y-4">
                    {reps.fields.map((rep, idx) => (
                      <Card 
                        key={rep.id} 
                        className="p-5 border-2"
                      >
                        <div className="flex justify-between items-center mb-4">
                          <h4 className="font-medium text-sm text-muted-foreground">Representante #{idx + 1}</h4>
                          <Button 
                            type="button" 
                            variant="ghost" 
                            size="icon"
                            className="text-destructive hover:bg-destructive/10 h-8 w-8"
                            onClick={() => reps.remove(idx)}
                          >
                            <Trash2 size={16} />
                          </Button>
                        </div>
                        <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
                          {[
                            {key: 'nome', label: t('buyerForm.fields.name')},
                            {key: 'departamento', label: t('buyerForm.fields.department')},
                            {key: 'celular', label: t('buyerForm.fields.mobile')},
                            {key: 'email', label: t('buyerForm.fields.email')}
                          ].map(({key, label}) => (
                            <div key={key}>
                              <Controller
                                name={`representantes.${idx}.${key}`}
                                control={control}
                                render={({ field }) => (
                                  <div className="space-y-2">
                                    <label htmlFor={`rep-${idx}-${key}`} className="text-sm font-semibold text-foreground">
                                      {label}
                                    </label>
                                    <Input 
                                      {...field} 
                                      id={`rep-${idx}-${key}`}
                                      className="h-11" 
                                    />
                                  </div>
                                )}
                              />
                            </div>
                          ))}
                        </div>
                      </Card>
                    ))}
                  </div>
                )}
              </Card>
            </TabsContent>

            {/* Tab: Dados Operacionais */}
            <TabsContent value="operational" className="space-y-6">
              <Card className="p-6">
                <h3 className="text-lg font-semibold mb-6">{t('buyerForm.sections.operational')}</h3>
                
                <div className="grid grid-cols-1 md:grid-cols-2 gap-6 mb-6">
                  {[
                    ['dadosOperacionais.segmento', t('buyerForm.fields.segment')],
                    ['dadosOperacionais.inicioParceria', t('buyerForm.fields.partnershipStart')]
                  ].map(([name, label]) => (
                    <div key={name}>
                      <Controller
                        name={name}
                        control={control}
                        render={({ field }) => (
                          <div className="space-y-2">
                            <label htmlFor={name.replace('.', '-')} className="text-sm font-semibold text-foreground">
                              {label}
                            </label>
                            <Input 
                              {...field} 
                              id={name.replace('.', '-')}
                              className="h-11" 
                              type={name.includes('inicio') ? 'date' : 'text'}
                            />
                          </div>
                        )}
                      />
                    </div>
                  ))}
                </div>

                <div>
                  <Controller
                    name="dadosOperacionais.observacoes"
                    control={control}
                    render={({ field }) => (
                      <div className="space-y-2">
                        <label htmlFor="observacoes" className="text-sm font-semibold text-foreground">
                          {t('buyerForm.fields.observations')}
                        </label>
                        <Textarea 
                          {...field}
                          id="observacoes"
                          rows={5}
                          className="resize-none" 
                          placeholder="Adicione observações sobre este comprador..."
                        />
                      </div>
                    )}
                  />
                </div>
              </Card>
            </TabsContent>
          </Tabs>

          {/* Footer */}
          <div className="flex justify-end gap-4 mt-8 pt-6 border-t">
            <Button
              type="button"
              variant="outline"
              onClick={() => navigate(-1)}
              className="px-6"
            >
              {t('buyerForm.actions.cancel')}
            </Button>
            <Button
              type="submit"
              className="px-6"
            >
              {t('buyerForm.actions.save')}
            </Button>
          </div>
        </form>
      </div>
  );
}